import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { useHeaderConfig } from '../hooks/useHeaderConfig';
import { HeaderLogo } from './header/HeaderLogo';
import { NavigationMenu } from './header/NavigationMenu';
import { HeaderActions } from './header/HeaderActions';
import { UserMenu } from './header/UserMenu';
import { MobileMenu } from './header/MobileMenu';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  
  const { isAuthenticated, user, userProfile, userRole, handleLogout } = useAuthStatus();
  const { headerConfig, navItems, navSubItems, siteConfig, categories, loading, configLoading } = useHeaderConfig();

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDropdownClick = (slug: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setOpenDropdown(openDropdown === slug ? null : slug);
  };

  const toggleUserDropdown = () => {
    setOpenDropdown(openDropdown === 'user' ? null : 'user');
  };
  
  const closeUserDropdown = () => {
    setOpenDropdown(null);
  };

  // Apply transparency based on config
  const getHeaderBackground = () => {
    if (!headerConfig) return '';
    
    const opacity = headerConfig.transparency_level ? headerConfig.transparency_level / 100 : 1;
    
    return isScrolled || isMenuOpen 
      ? `rgba(${hexToRgb(headerConfig.background_color || '#ffffff')}, 1)` 
      : `rgba(${hexToRgb(headerConfig.background_color || '#ffffff')}, ${opacity})`;
  };

  const getDarkHeaderBackground = () => {
    if (!headerConfig) return '';
    
    const opacity = headerConfig.transparency_level ? headerConfig.transparency_level / 100 : 1;
    
    return isScrolled || isMenuOpen
      ? `rgba(${hexToRgb(headerConfig.background_dark_color || '#0f172a')}, 1)` 
      : `rgba(${hexToRgb(headerConfig.background_dark_color || '#0f172a')}, ${opacity})`;
  };

  // Helper function to convert hex to rgb
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '255, 255, 255';
  };

  // Determine position class based on config
  const getPositionClass = () => {
    if (!headerConfig) return 'fixed lg:sticky';
    return headerConfig.fixed_position ? 'fixed lg:sticky' : 'relative';
  };

  // Get display name for user (used in mobile menu)
  const getUserDisplayName = () => {
    return userProfile?.full_name || user?.email || 'Usuário';
  };

  // Check if user is admin (used in mobile menu)
  const isAdmin = userRole?.role_name === 'Admin';

  if (configLoading) {
    return (
      <header className="fixed lg:sticky top-0 left-0 right-0 z-50 w-full bg-white dark:bg-slate-950">
        <div className="max-w-full mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between py-2 sm:py-3 md:py-5">
            <div className="h-7 w-36 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
            <div className="flex items-center space-x-2">
              <div className="h-9 w-9 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-full"></div>
              <div className="h-9 w-9 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-full"></div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      className={`${getPositionClass()} top-0 left-0 right-0 z-50 w-full transition-colors duration-200`}
      style={{
        backgroundColor: getHeaderBackground(),
        color: headerConfig?.text_color || '#1e293b',
        '--header-dark-bg': getDarkHeaderBackground(),
        '--header-dark-text': headerConfig?.text_dark_color || '#f1f5f9',
      } as React.CSSProperties}
    >
      <div className="max-w-full mx-auto px-2 sm:px-4">
        <div className="flex items-center justify-between py-2 sm:py-3 md:py-5">
          {/* Logo */}
          <HeaderLogo 
            logoUrl={headerConfig?.logo_url} 
            siteName={siteConfig.site_name}
            siteSubtitle={siteConfig.site_subtitle}
          />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 px-2 lg:px-8">
            <NavigationMenu 
              navItems={navItems}
              navSubItems={navSubItems}
              categories={categories.filter(cat => cat.show_in_header !== false)} // Filter out categories that should be hidden
              loading={loading}
            />
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 sm:space-x-1">
            <HeaderActions 
              showSearch={headerConfig?.show_search || false}
              showThemeToggle={headerConfig?.show_theme_toggle || false}
            />
            
            {/* User Menu */}
            <UserMenu 
              isAuthenticated={isAuthenticated}
              user={user}
              userProfile={userProfile}
              isOpen={openDropdown === 'user'}
              onToggle={toggleUserDropdown}
              onClose={closeUserDropdown}
            />

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 pr-5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            >
              {isMenuOpen ? (
                <X size={20} className="text-gray-700 dark:text-gray-100" />
              ) : (
                <Menu size={20} className="text-gray-700 dark:text-gray-100" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <MobileMenu 
          isOpen={isMenuOpen}
          isAuthenticated={isAuthenticated}
          userDisplayName={getUserDisplayName()}
          isAdmin={isAdmin}
          userRole={userRole?.role_name}
          navItems={navItems}
          navSubItems={navSubItems}
          categories={categories.filter(cat => cat.show_in_header !== false)} // Filter out categories that should be hidden
          openDropdown={openDropdown}
          handleDropdownClick={handleDropdownClick}
          onClose={() => setIsMenuOpen(false)}
          onLogout={handleLogout}
        />
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        /* Add custom CSS for dark mode */
        .dark header {
          background-color: var(--header-dark-bg) !important;
          color: var(--header-dark-text) !important;
        }
        `
      }} />
    </header>
  );
}